import { radius, shadows } from '@alison/ui';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import type { AdNetworkStatus, AdProvider } from '../../lib/adNetwork.hooks.js';
import { linkSocial } from '../../lib/authClient.js';
import { ConnectDrawer } from '../integrations/ConnectDrawer.js';
import { NetworkMark } from '../integrations/NetworkMark.js';
import {
  authorizationErrorMessage,
  OAUTH_PROVIDER,
  PROVIDER_DESCRIPTION,
  PROVIDER_LABEL,
} from '../integrations/networks.js';
import { StatusChip } from '../integrations/StatusChip.js';

export interface ConnectNetworkProps {
  provider: AdProvider;
  productId: number;
  status: { data: AdNetworkStatus | undefined; isPending: boolean; isError: boolean };
  /** Ad accounts already syncing, for the box's summary line. */
  accounts: number;
  // Back from the network's consent screen: open the account picker straight away.
  returned: boolean;
  returnError: string | null;
}

// One network as a box, behaving like Settings > Integrations: an unauthorized network goes
// straight to its consent screen, and an authorized one opens the account drawer (which also
// opens by itself on the way back). Only the way back lands on /onboarding.
export function ConnectNetwork({
  provider,
  productId,
  status,
  accounts,
  returned,
  returnError,
}: ConnectNetworkProps): ReactNode {
  const [open, setOpen] = useState(returned);
  const [error, setError] = useState<string | null>(
    returnError === null ? null : authorizationErrorMessage(returnError),
  );
  const [leaving, setLeaving] = useState(false);
  const label = PROVIDER_LABEL[provider];
  const state = status.data?.state ?? 'not_connected';
  const authorized = state !== 'not_connected' && state !== 'needs_reauth';

  async function connect(): Promise<void> {
    setError(null);
    setLeaving(true);
    const base = `${window.location.origin}/onboarding`;
    const flow = `provider=${provider}&productId=${productId}`;
    const result = await linkSocial({
      provider: OAUTH_PROVIDER[provider],
      callbackURL: `${base}?connected=1&${flow}`,
      errorCallbackURL: `${base}?${flow}`,
    });
    if (result.data?.url) {
      window.location.assign(result.data.url);
      return;
    }
    setLeaving(false);
    setError(result.error?.message ?? 'Could not start authorization');
  }

  return (
    <>
      <ButtonBase
        component="article"
        data-testid={`onboarding-network-${provider}`}
        aria-label={authorized ? `Manage ${label}` : `Connect ${label}`}
        disabled={leaving}
        onClick={() => (authorized ? setOpen(true) : void connect())}
        sx={{
          p: 3,
          minHeight: 176,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          gap: 1.5,
          textAlign: 'left',
          border: '1px solid',
          borderColor: accounts > 0 ? 'success.main' : 'divider',
          borderRadius: `${radius.xl}px`,
          bgcolor: 'background.paper',
          boxShadow: accounts > 0 ? shadows.card : 'none',
          transition: 'border-color 150ms, box-shadow 150ms',
          '&:hover': { borderColor: accounts > 0 ? 'success.main' : 'primary.light', boxShadow: 1 },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <NetworkMark provider={provider} size={40} />
        <Typography component="h2" sx={{ fontSize: 14, fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography sx={{ flex: 1, fontSize: 12, color: 'text.secondary' }}>
          {accounts > 0
            ? `${accounts} ad ${accounts === 1 ? 'account' : 'accounts'} syncing to Alison`
            : PROVIDER_DESCRIPTION[provider]}
        </Typography>
        {/* Always takes its line, so a failed connect doesn't grow the box. */}
        <Typography
          role={error === null ? undefined : 'alert'}
          sx={{ minHeight: 18, fontSize: 12, color: 'error.main' }}
        >
          {error}
        </Typography>
        {leaving ? (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Redirecting…</Typography>
        ) : (
          <StatusChip state={state} provider={provider} />
        )}
      </ButtonBase>
      <ConnectDrawer
        open={open}
        onClose={() => setOpen(false)}
        provider={provider}
        productId={productId}
        status={status.data}
        statusPending={status.isPending}
        statusError={status.isError}
      />
    </>
  );
}
